import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, take } from 'rxjs';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { InsightType } from 'src/core/models/generic-lookup-type/insight/insight-type.glt';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { LLMConfigurationDto } from 'src/core/models/llm/llm-configuration.dto';
import { CategoryDto } from 'src/core/models/query/category.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';

@Component({
  selector: 'ca-ai-insight-settings',
  templateUrl: './ai-insight-settings.component.html',
  styleUrls: ['./ai-insight-settings.component.scss'],
  standalone: false,
})
export class AiInsightSettingsComponent implements OnInit {
  aiInsightSettingsForm: FormGroup;
  aiInsightSettingDtos: ConfigurationSettingsDto[] = [];
  providerTypes: GenericLookupDto[];
  llmConfigurations: LLMConfigurationDto[] = [];
  insightTypes: GenericLookupDto[];
  insightTypesData: string[] = [];
  languagesDataSource: CallLanguageCodeDto[];
  defaultLanguageCode: string;
  languageCode: string;

  private readonly settingKeyInsightTypes = 'Insight.InsightType';
  private readonly settingKeyLanguageCode = 'Insight.LanguageCode';
  private readonly settingKeyLLMConfigurationId = 'Insight.LLMConfigurationId';
  private readonly settingKeyCustomizeSummarizationEnabled =
    'Insight.CustomizeSummarizationEnabled';
  private readonly settingKeyPromptPrefix = 'Insight.PromptPrefix';
  private readonly automaticSummarizationPrefix = 'AutomaticSummarization';
  private readonly automaticSummarizationEnabled = `${this.automaticSummarizationPrefix}.Enabled`;
  private readonly automaticSummarizationFilterConversations = `${this.automaticSummarizationPrefix}.FilterConversations`;
  private readonly automaticSummarizationCategories = `${this.automaticSummarizationPrefix}.Categories`;
  private readonly automaticSummarizationDailyConversationLimit = `${this.automaticSummarizationPrefix}.DailyConversationLimit`;

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private store: Store,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private localizationService: LocalizationService,
    private crudService: CrudService,
    private operators: Operators
  ) {
    this.crudService
      .get<LLMConfigurationDto>(LLMConfigurationDto, {
        filters: [],
        sorters: [],
        skipCount: 0,
        maxResultCount: 9999,
      })
      .subscribe(response => {
        this.llmConfigurations = response.items;
      });

    const defaultValues = ['Insight.Type.Topic', 'Insight.Type.Summary'];

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(
        map(filterFn => filterFn(InsightType)),
        map(result =>
          result.map(item => ({
            ...item,
            disabled: defaultValues.includes(item.code),
          }))
        )
      )
      .subscribe(modifiedResult => {
        this.insightTypes = modifiedResult;
      });
    let request = {
      filters: [
        {
          field: 'isVisible',
          operator: this.operators.Contains,
          value: true,
        },
        {
          field: 'withoutMixed',
          operator: this.operators.Contains,
          value: true,
        },
      ],
      sorters: [],
      maxResultCount: 99999,
      skipCount: 0,
    };
    this.settingService
      .getLanguageCodeList(request)
      .pipe(take(1))
      .subscribe(res => {
        this.languagesDataSource = res.items;
      });

    let automaticSummarizationFilterConversations = this.config
      .getSetting(this.automaticSummarizationFilterConversations)
      ?.toLowerCase();

    let insightData = this.config.getSetting(this.settingKeyInsightTypes);
    let selectedSet = new Set<string>();

    if (insightData) {
      insightData
        .split(';')
        .filter(x => x.length > 0)
        .forEach(code => selectedSet.add(code));
    }

    defaultValues.forEach(code => selectedSet.add(code));
    this.insightTypesData = Array.from(selectedSet);

    automaticSummarizationFilterConversations =
      automaticSummarizationFilterConversations === null
        ? 'false'
        : automaticSummarizationFilterConversations;

    this.defaultLanguageCode = this.config.getSetting('Global.SystemLanguageCode');
    let languageCode = this.config.getSetting(this.settingKeyLanguageCode);
    if (!languageCode) {
      this.languageCode = this.defaultLanguageCode;
    } else {
      this.languageCode = languageCode;
    }
    let llmConfigurationId;
    if (this.config.getSetting(this.settingKeyLLMConfigurationId)) {
      llmConfigurationId = Number(this.config.getSetting(this.settingKeyLLMConfigurationId));
    }

    this.aiInsightSettingsForm = fb.group({
      insightTypes: [this.insightTypesData],
      languageCode: this.languageCode,
      llmConfigurationId: [llmConfigurationId],
      promptPrefix: [this.config.getSetting(this.settingKeyPromptPrefix)],
      customizeSummarizationEnabled: [
        JSON.parse(
          this.config.getSetting(this.settingKeyCustomizeSummarizationEnabled).toLowerCase()
        ),
      ],
      automaticSummarizationEnabled: [
        JSON.parse(this.config.getSetting(this.automaticSummarizationEnabled).toLowerCase()),
      ],
      automaticSummarizationFilterConversations: [automaticSummarizationFilterConversations],
      automaticSummarizationCategories: [null],
      automaticSummarizationDailyConversationLimit: [
        this.config.getSetting(this.automaticSummarizationDailyConversationLimit),
      ],
    });

    if (this.insightTypesData.length > 0) {
      this.aiInsightSettingsForm.get('insightTypes').setValue(this.insightTypesData);
    }

    this.loadCategories();
  }

  ngOnInit(): void {}

  onSubmitAiInsightSettings() {
    if (this.aiInsightSettingsForm.invalid) {
      return;
    }

    this.aiInsightSettingDtos = new Array();

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyInsightTypes,
      settingValue: this.aiInsightSettingsForm.get('insightTypes').value?.join(';'),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyLanguageCode,
      settingValue: this.aiInsightSettingsForm.get('languageCode').value.toString(),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyLLMConfigurationId,
      settingValue: this.aiInsightSettingsForm.get('llmConfigurationId').value?.toString(),
    });

    this.aiInsightSettingDtos.push({
      settingName: this.settingKeyCustomizeSummarizationEnabled,
      settingValue: this.aiInsightSettingsForm
        .get('customizeSummarizationEnabled')
        .value.toString(),
    });

    if (this.aiInsightSettingsForm.get('customizeSummarizationEnabled').value) {
      this.aiInsightSettingDtos.push({
        settingName: this.settingKeyPromptPrefix,
        settingValue: this.aiInsightSettingsForm.get('promptPrefix').value.toString(),
      });
    }

    this.aiInsightSettingDtos.push({
      settingName: this.automaticSummarizationEnabled,
      settingValue: this.aiInsightSettingsForm
        .get('automaticSummarizationEnabled')
        .value.toString(),
    });

    if (this.aiInsightSettingsForm.get('automaticSummarizationEnabled').value) {
      this.aiInsightSettingDtos.push({
        settingName: this.automaticSummarizationFilterConversations,
        settingValue: this.aiInsightSettingsForm.get('automaticSummarizationFilterConversations')
          .value,
      });

      this.aiInsightSettingDtos.push({
        settingName: this.automaticSummarizationCategories,
        settingValue: JSON.stringify(
          this.aiInsightSettingsForm.get('automaticSummarizationCategories').value?.map(x => x.id)
        ),
      });

      this.aiInsightSettingDtos.push({
        settingName: this.automaticSummarizationDailyConversationLimit,
        settingValue: this.aiInsightSettingsForm
          .get('automaticSummarizationDailyConversationLimit')
          .value.toString(),
      });

      this.aiInsightSettingDtos.push({
        settingName: this.settingKeyPromptPrefix,
        settingValue: this.aiInsightSettingsForm.get('promptPrefix').value.toString(),
      });
    }

    this.settingService.saveSetting(this.aiInsightSettingDtos).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully')
      );
    });
  }

  isDisabled(code: string) {
    if (code === 'Insight.Type.Topic' || code === 'Insight.Type.Summary') {
      return true;
    }

    return false;
  }

  removeFromSelectedInsights(insightCode: string) {
    let selectedItems = this.aiInsightSettingsForm.get('insightTypes').value;

    if (selectedItems) {
      const indexToRemoveFromSelected = selectedItems.findIndex(i => i === insightCode);
      if (indexToRemoveFromSelected > -1) {
        selectedItems.splice(indexToRemoveFromSelected, 1);
        this.aiInsightSettingsForm.get('insightTypes').setValue(selectedItems);
      }
    }
  }

  private loadCategories(): void {
    const categorySettingValue = this.config.getSetting(this.automaticSummarizationCategories);
    const categoryIds = JSON.parse(categorySettingValue) as string[];

    if (categoryIds && categoryIds.length > 0) {
      this.crudService
        .get<CategoryDto>(CategoryDto, {
          filters: [
            {
              field: 'id',
              operator: this.operators.In,
              value: categoryIds,
            },
          ],
          sorters: [],
          skipCount: 0,
          maxResultCount: 9999,
        })
        .subscribe(response => {
          this.aiInsightSettingsForm
            .get('automaticSummarizationCategories')
            .setValue(response.items);
        });
    }
  }
}
